import React, { useEffect, useRef } from 'react'
import styled, { css } from 'styled-components'
import {
  MarkdownTextAreaProps,
  StyledMarkdownTextAreaProps,
} from './markdown-text-area.types'
import { RichTextEditor as MantineRichTextEditor, Link } from '@mantine/tiptap'
import '@mantine/tiptap/styles.css'
import { useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Highlight from '@tiptap/extension-highlight'
import Underline from '@tiptap/extension-underline'
import Placeholder from '@tiptap/extension-placeholder'
import { Markdown } from 'tiptap-markdown'
import { styledVariant } from '../../util'
import { inputStatusStyles } from '../../shared/input-status'

export const MarkdownTextAreaWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
`

export const StyledTextAreaEditor = styled(
  MantineRichTextEditor
)<StyledMarkdownTextAreaProps>`
  ${({ theme, $disabled }) => css`
    display: flex;
    flex-direction: column;
    width: 100%;
    background: #fff;
    border: 1px solid ${theme.colors.gray80};
    max-height: 150px;
    overflow: hidden;
    &:focus-within,
    &:hover {
      border-color: ${theme.colors.gray200};
    }

    ${styledVariant('$status', inputStatusStyles)};

    .mantine-RichTextEditor-toolbar {
      flex-shrink: 0;
      position: relative;
      z-index: 1;
      padding: 6px;
      border: none;
      background: #fff;

      .mantine-RichTextEditor-control {
        border: none;
        border-radius: ${theme.borderRadii.small};
        &[data-active] {
          background: ${theme.colors.primary100};
          color: ${theme.colors.primary400};
        }
      }

      > div {
        gap: 2px;
      }
    }

    ${$disabled &&
    css`
      pointer-events: none;
      background-color: ${theme.colors.gray80};
      outline: none;
      border: none;

      .mantine-RichTextEditor-toolbar,
      .mantine-RichTextEditor-control,
      .mantine-RichTextEditor-controlsGroup,
      .mantine-RichTextEditor-content {
        background-color: ${theme.colors.gray80};
      }
    `}
  `}
`

export const StyledTextAreaContent = styled(MantineRichTextEditor.Content)`
  ${({ theme }) => css`
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    color: ${theme.colors.gray600};
    font-size: ${theme.fontSizes.bodySmall};
    padding-bottom: ${theme.sizes[4]};
    .ProseMirror {
      padding: 0 ${theme.sizes[4]} ${theme.sizes[2]};
    }

    a {
      color: ${theme.colors.primary400};
    }
  `}
`

export const Divider = styled.div`
  width: ${({ theme }) => theme.sizes[0]};
  height: ${({ theme }) => theme.sizes[4]};
  margin: ${({ theme }) => theme.sizes[1]} ${({ theme }) => theme.sizes[0]};
  background: ${({ theme }) => theme.colors.gray80};
`

export const MarkdownTextArea = ({
  status = 'default',
  placeholder,
  content = '',
  testId = 'markdown-text-area',
  disabled = false,
  maxLength,
  onChange,
  ...props
}: MarkdownTextAreaProps) => {
  const richTextEditor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        HTMLAttributes: {
          target: '_blank',
        },
      }),
      Highlight,
      Placeholder.configure({ placeholder }),
      Markdown,
    ],
    content,
    onUpdate: ({ editor }) => {
      const content = editor.storage.markdown.getMarkdown()
      const formattedContent = content.replace(/\\\n/g, '  \n')

      onChange?.(formattedContent)
    },
    editorProps: {
      transformPastedHTML(html) {
        // Strip all HTML tags to allow only plain text
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = html
        return tempDiv.textContent || tempDiv.innerText || ''
      },
      handleTextInput(view, from, to, text) {
        if (maxLength) {
          const currentLength = view.state.doc.textContent.length
          const selectionLength = to - from
          const newLength = currentLength - selectionLength + text.length

          if (newLength > maxLength) {
            return true
          }
        }
        return false
      },
      handlePaste(view, event) {
        if (maxLength) {
          const clipboardData = event.clipboardData
          if (clipboardData) {
            event.preventDefault()
            const text = clipboardData.getData('text/plain')
            const currentLength = view.state.doc.textContent.length
            const availableSpace = maxLength - currentLength

            if (availableSpace > 0) {
              const truncatedText = text.slice(0, availableSpace)
              const { state } = view
              const { tr } = state
              tr.insertText(truncatedText)
              view.dispatch(tr)
            }
            return true
          }
        }
        return false
      },
    },
  })

  const hasSeededContent = useRef(false)

  // useEditor only reads `content` on creation; seed once if it arrives after mount.
  useEffect(() => {
    if (!richTextEditor || hasSeededContent.current || !content) {
      return
    }

    if (!richTextEditor.storage.markdown?.getMarkdown?.()) {
      richTextEditor.commands.setContent(content)
    }

    hasSeededContent.current = true
  }, [content, richTextEditor])

  if (!richTextEditor) return null

  return (
    <MarkdownTextAreaWrapper data-testid={testId}>
      <StyledTextAreaEditor
        {...props}
        $status={status}
        $disabled={disabled}
        editor={richTextEditor}
        data-testid="markdown-text-area-editor"
      >
        <MantineRichTextEditor.Toolbar>
          <MantineRichTextEditor.ControlsGroup>
            <MantineRichTextEditor.Bold />
            <MantineRichTextEditor.Italic />
            <MantineRichTextEditor.Underline />
            <MantineRichTextEditor.Strikethrough />
            <Divider />
            <MantineRichTextEditor.Link />
            <MantineRichTextEditor.Unlink />
          </MantineRichTextEditor.ControlsGroup>
        </MantineRichTextEditor.Toolbar>
        <StyledTextAreaContent data-testid="markdown-text-area-content" />
      </StyledTextAreaEditor>
    </MarkdownTextAreaWrapper>
  )
}
